const shell = document.querySelector('[data-antlitzwerk-vto-shell]');
const mount = document.querySelector('#antlitzwerk-lipstick-try-on');
const editorial = document.querySelector('[data-vto-editorial]');

if (shell && mount) {
  const cameraButton = shell.querySelector('[data-vto-shell-allow]');
  const photoButton = shell.querySelector('[data-vto-shell-photo]');
  const photoInput = shell.querySelector('[data-vto-shell-photo-input]');
  const backLink = shell.querySelector('[data-vto-shell-back]');
  const scrollCue = shell.querySelector('[data-vto-scroll-cue]');
  const status = shell.querySelector('[data-vto-shell-status]');
  const statusText = status?.querySelector('span:last-child');
  const startupClass = 'antlitzwerk-vto-starting';
  const hadInitialBodyPhase = Object.prototype.hasOwnProperty.call(document.body.dataset, 'appPhase');
  const initialBodyPhase = document.body.dataset.appPhase;

  let pendingStream = null;
  let assetsPromise = null;
  let busy = false;
  let active = false;
  let disposed = false;

  mount.dataset.vtoBootstrapManaged = 'true';
  if (mount.dataset.workerJavascript && !mount.dataset.workerUrl) {
    mount.dataset.workerUrl = mount.dataset.workerJavascript;
  }

  const absoluteUrl = (href) => new URL(href, document.baseURI).href;
  const stopStream = (stream) => stream?.getTracks?.().forEach((track) => track.stop());

  const suppressInternalConsent = () => {
    const internalConsent = mount.querySelector('#camera-consent');
    if (!internalConsent) return;
    internalConsent.hidden = true;
    internalConsent.setAttribute('aria-hidden', 'true');
    internalConsent.inert = true;
  };

  const consentObserver = new MutationObserver(suppressInternalConsent);
  consentObserver.observe(mount, { childList: true, subtree: true });

  const restoreOuterBodyState = () => {
    if (hadInitialBodyPhase) document.body.dataset.appPhase = initialBodyPhase;
    else delete document.body.dataset.appPhase;
    document.body.classList.remove('antlitzwerk-vto-processing');
  };

  const supportsCoreRuntime = () => (
    typeof WebAssembly === 'object' &&
    typeof WebAssembly.instantiate === 'function' &&
    typeof Promise === 'function' &&
    typeof URL === 'function' &&
    typeof HTMLCanvasElement === 'function'
  );

  const setStatus = (message, state = 'idle') => {
    if (statusText && message) statusText.textContent = message;
    if (status) status.hidden = state === 'idle' || !message;
    shell.classList.toggle('is-loading', state === 'loading');
    shell.classList.toggle('has-error', state === 'error');
  };

  const setBusy = (value) => {
    busy = Boolean(value);
    [cameraButton, photoButton].filter(Boolean).forEach((button) => {
      button.disabled = busy;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  };

  const prepareHiddenMount = () => {
    mount.hidden = false;
    mount.style.visibility = 'hidden';
    mount.style.position = 'fixed';
    mount.style.inset = '0';
    mount.style.width = '100%';
    mount.style.height = '100%';
    mount.style.pointerEvents = 'none';
    mount.style.overflow = 'hidden';
    suppressInternalConsent();
  };

  const concealMount = () => {
    mount.style.visibility = 'hidden';
    mount.style.position = 'fixed';
    mount.style.inset = '0';
    mount.style.width = '100%';
    mount.style.height = '100%';
    mount.style.pointerEvents = 'none';
    mount.style.overflow = 'hidden';
  };

  const activateMount = () => {
    active = true;
    globalThis.__ANTLITZWERK_VTO_BOOTSTRAP_ACTIVE__ = false;
    globalThis.__ANTLITZWERK_VTO_AUTO_START__ = false;
    globalThis.__ANTLITZWERK_VTO_PREWARM_PROMISE__ = null;
    globalThis.__ANTLITZWERK_VTO_START_PROMISE__ = null;
    document.body.classList.remove(startupClass);
    document.body.classList.add('antlitzwerk-vto-active');
    shell.hidden = true;
    if (editorial) editorial.hidden = true;
    mount.hidden = false;
    mount.style.visibility = '';
    mount.style.position = '';
    mount.style.inset = '';
    mount.style.width = '';
    mount.style.height = '';
    mount.style.pointerEvents = '';
    mount.style.overflow = '';
    pendingStream = null;
    setBusy(false);
  };

  const loadStylesheet = (href) => new Promise((resolve, reject) => {
    const target = absoluteUrl(href);
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')]
      .find((link) => link.href === target);

    if (existing?.sheet) {
      resolve();
      return;
    }
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => reject(new Error('VTO stylesheet failed.')), { once: true });
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = target;
    link.dataset.antlitzwerkVtoAsset = 'style';
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', () => {
      link.remove();
      reject(new Error('VTO stylesheet failed.'));
    }, { once: true });
    document.head.append(link);
  });

  const waitForAppReady = () => {
    if (globalThis.__ANTLITZWERK_VTO_APP_READY__) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        globalThis.removeEventListener('antlitzwerk:vto-app-ready', handleReady);
        reject(new Error('VTO application did not mount.'));
      }, 15_000);
      const handleReady = () => {
        clearTimeout(timeout);
        resolve();
      };
      globalThis.addEventListener('antlitzwerk:vto-app-ready', handleReady, { once: true });
    });
  };

  const loadApplication = () => {
    if (assetsPromise) return assetsPromise;
    const appJavascript = mount.dataset.appJavascript;
    const vendorJavascript = mount.dataset.vendorJavascript;
    const appStylesheet = mount.dataset.appStylesheet;
    if (!appJavascript || !vendorJavascript || !appStylesheet) {
      return Promise.reject(new Error('Required VTO assets are missing.'));
    }

    prepareHiddenMount();
    const readyPromise = waitForAppReady();
    assetsPromise = Promise.all([
      loadStylesheet(appStylesheet),
      import(absoluteUrl(vendorJavascript)),
    ])
      .then(() => import(absoluteUrl(appJavascript)))
      .then(() => readyPromise)
      .then(() => {
        suppressInternalConsent();
        if (!busy && !active) restoreOuterBodyState();
        return true;
      })
      .catch((error) => {
        assetsPromise = null;
        throw error;
      });
    return assetsPromise;
  };

  const resetPrewarmedCamera = () => {
    stopStream(pendingStream);
    pendingStream = null;
    const prewarmed = globalThis.__ANTLITZWERK_VTO_PREWARMED_STREAM__;
    if (prewarmed) stopStream(prewarmed);
    globalThis.__ANTLITZWERK_VTO_PREWARMED_STREAM__ = null;
    globalThis.__ANTLITZWERK_VTO_PREWARM_PROMISE__ = null;
    globalThis.__ANTLITZWERK_VTO_AUTO_START__ = false;
    globalThis.__ANTLITZWERK_VTO_START_PROMISE__ = null;
  };

  const showFailure = (message) => {
    resetPrewarmedCamera();
    globalThis.__ANTLITZWERK_VTO_BOOTSTRAP_ACTIVE__ = false;
    document.body.classList.remove('antlitzwerk-vto-active', startupClass);
    restoreOuterBodyState();
    shell.hidden = false;
    if (editorial) editorial.hidden = false;
    concealMount();
    suppressInternalConsent();
    setBusy(false);
    setStatus(message || mount.dataset.shellLoadError || '', 'error');
    requestAnimationFrame(() => cameraButton?.focus({ preventScroll: true }));
  };

  const previewConstraints = () => {
    const memory = Number(navigator.deviceMemory) || 0;
    const cores = Number(navigator.hardwareConcurrency) || 0;
    const efficient = (memory > 0 && memory <= 4) || (cores > 0 && cores <= 4);
    return {
      audio: false,
      video: {
        facingMode: 'user',
        width: { ideal: efficient ? 1280 : 1920 },
        height: { ideal: efficient ? 960 : 1440 },
        aspectRatio: { ideal: 4 / 3 },
        frameRate: { ideal: 30, min: 20, max: 30 },
        resizeMode: { ideal: 'none' },
      },
    };
  };

  const startCamera = async () => {
    if (busy || active || disposed) return;
    if (!supportsCoreRuntime()) {
      showFailure(mount.dataset.shellUnsupported || mount.dataset.shellLoadError || '');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      showFailure(mount.dataset.shellCameraError || '');
      return;
    }

    setBusy(true);
    globalThis.__ANTLITZWERK_VTO_BOOTSTRAP_ACTIVE__ = true;
    globalThis.__ANTLITZWERK_VTO_AUTO_START__ = true;
    document.body.classList.add(startupClass);
    document.body.dataset.appPhase = 'alignment';
    setStatus(mount.dataset.shellLoading || '', 'loading');

    try {
      // The permission request begins synchronously from the trusted click.
      const cameraPromise = navigator.mediaDevices.getUserMedia(previewConstraints()).then((stream) => {
        if (disposed) {
          stopStream(stream);
          const error = new Error('Page closed during camera start.');
          error.name = 'AbortError';
          throw error;
        }
        pendingStream = stream;
        globalThis.__ANTLITZWERK_VTO_PREWARMED_STREAM__ = stream;
        return stream;
      });
      globalThis.__ANTLITZWERK_VTO_PREWARM_PROMISE__ = cameraPromise;

      const [stream] = await Promise.all([cameraPromise, loadApplication()]);
      if (disposed) {
        stopStream(stream);
        return;
      }

      suppressInternalConsent();
      const automaticStart = globalThis.__ANTLITZWERK_VTO_START_PROMISE__;
      const startApplication = globalThis.__ANTLITZWERK_VTO_START__;
      const started = automaticStart && typeof automaticStart.then === 'function'
        ? await automaticStart
        : typeof startApplication === 'function'
          ? await startApplication()
          : false;

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      suppressInternalConsent();
      const camera = mount.querySelector('#camera');
      const track = camera?.srcObject?.getVideoTracks?.()[0];
      const ready = Boolean(
        started !== false &&
        camera?.srcObject &&
        track?.readyState === 'live' &&
        camera.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
      );
      if (!ready) {
        const error = new Error('Camera did not reach a ready state.');
        error.name = 'NotReadableError';
        throw error;
      }
      activateMount();
    } catch (error) {
      if (disposed || error?.name === 'AbortError') return;
      const cameraErrors = new Set([
        'NotAllowedError', 'NotFoundError', 'NotReadableError',
        'OverconstrainedError', 'SecurityError', 'TypeError',
      ]);
      showFailure(cameraErrors.has(error?.name)
        ? mount.dataset.shellCameraError
        : mount.dataset.shellLoadError);
    }
  };

  const startPhoto = async (file) => {
    if (!file || busy || active || disposed) return;
    if (!supportsCoreRuntime()) {
      showFailure(mount.dataset.shellUnsupported || mount.dataset.shellLoadError || '');
      return;
    }

    setBusy(true);
    globalThis.__ANTLITZWERK_VTO_BOOTSTRAP_ACTIVE__ = true;
    globalThis.__ANTLITZWERK_VTO_AUTO_START__ = false;
    document.body.classList.add(startupClass);
    document.body.dataset.appPhase = 'alignment';
    setStatus(mount.dataset.shellPhotoLoading || mount.dataset.shellLoading || '', 'loading');

    try {
      await loadApplication();
      if (disposed) return;
      suppressInternalConsent();
      const importPhoto = globalThis.__ANTLITZWERK_VTO_IMPORT_PHOTO__;
      if (typeof importPhoto !== 'function') throw new Error('Photo import is unavailable.');
      const imported = await importPhoto(file);
      if (!imported) throw new Error('Photo import was not completed.');
      activateMount();
    } catch (error) {
      if (disposed || error?.name === 'AbortError') return;
      showFailure(mount.dataset.shellPhotoError || mount.dataset.shellLoadError || '');
    } finally {
      if (photoInput) photoInput.value = '';
    }
  };

  backLink?.addEventListener('click', (event) => {
    try {
      const referrer = document.referrer ? new URL(document.referrer) : null;
      if (referrer?.origin === window.location.origin && history.length > 1) {
        event.preventDefault();
        history.back();
      }
    } catch {
      // The locale-aware product link remains the safe fallback.
    }
  });

  scrollCue?.addEventListener('click', (event) => {
    if (!editorial) return;
    event.preventDefault();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    editorial.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  cameraButton?.addEventListener('click', startCamera);
  photoButton?.addEventListener('click', () => {
    if (busy || active) return;
    if (photoInput) {
      photoInput.value = '';
      photoInput.click();
    }
  });
  photoInput?.addEventListener('change', () => {
    const file = photoInput.files?.[0];
    if (file) startPhoto(file);
  });

  // On this dedicated page, quietly prepare only the small interface/runtime
  // code. The 3.6 MB model and camera remain untouched until an explicit choice.
  const connection = navigator.connection;
  const mayWarm = !connection?.saveData && !/^(?:slow-)?2g$/i.test(connection?.effectiveType || '');
  const warm = () => {
    if (!mayWarm || busy || active || disposed) return;
    globalThis.__ANTLITZWERK_VTO_AUTO_START__ = false;
    loadApplication().catch(() => undefined);
  };
  [cameraButton, photoButton].filter(Boolean).forEach((button) => {
    button.addEventListener('pointerenter', warm, { once: true, passive: true });
    button.addEventListener('focus', warm, { once: true, passive: true });
    button.addEventListener('touchstart', warm, { once: true, passive: true });
  });
  if (mayWarm) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2600 });
    else window.setTimeout(warm, 1400);
  }

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    consentObserver.disconnect();
    resetPrewarmedCamera();
    globalThis.__ANTLITZWERK_VTO_BOOTSTRAP_ACTIVE__ = false;
    document.body.classList.remove('antlitzwerk-vto-active', startupClass);
    restoreOuterBodyState();
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) window.location.reload();
  });
  window.addEventListener('pagehide', cleanup, { once: true });
  window.addEventListener('beforeunload', cleanup, { once: true });
}
